// biome-ignore lint/style/useNodejsImportProtocol: <explanation>
import { Buffer } from 'buffer'
import { Instance, SnapshotOut, types } from 'mobx-state-tree'

import { GameStates, Table, TableSnapshotter } from '@ppwr/poker'

import { GameSnapshotModel } from '../api-store'
import { withRootStore } from '../extensions'
import {
  WalkthroughCommunityCards,
  WalkthroughPlayers,
  Walkthroughs,
  WalkthroughType,
  WalkthroughTypes
} from './walkthrough'

global.Buffer = Buffer

const TableTourStepsPositions = {
  no_of_suits: 1,
  cards_in_suit: 2,
  layout: 3,
  D: 6,
  SB: 7,
  BB: 8,
  hole: 9,
  chips: 10,
  pot: 11
} as const
export type TableTourStepsPositionKey = keyof typeof TableTourStepsPositions

type Position = {
  x: number
  y: number
  width: number
  height: number
}

const TargetPositionModel = types.model({
  step: types.number,
  position: types.model({
    x: types.number,
    y: types.number,
    width: types.number,
    height: types.number
  })
})

export const GameWalkthrough = types
  .model({
    walkthroughType: types.maybe(types.enumeration('WalkthroughType', Array.from(WalkthroughTypes))),
    step: types.optional(types.number, 0),
    targetPositions: types.array(TargetPositionModel),
    isResponseVisible: types.optional(types.boolean, true)
  })
  .extend(withRootStore)
  .views((self) => ({
    get isWalkthrough() {
      return Boolean(self.walkthroughType)
    },
    get currentStep() {
      if (!self.walkthroughType) return null
      const currentSteps = Walkthroughs[self.walkthroughType]
      return currentSteps[self.step]
    },
    get currentPosition() {
      return self.targetPositions.find((p) => p.step === self.step)?.position
    },
    get isFirstStep() {
      if (!self.walkthroughType) return false
      return self.step === 0
    },
    get isPreviousToLastStep() {
      if (!self.walkthroughType) return false
      return self.step === Walkthroughs[self.walkthroughType].length - 2
    },
    get isLastStep() {
      if (!self.walkthroughType) return false
      return self.step === Walkthroughs[self.walkthroughType].length - 1
    },
    get walkthroughProgress() {
      if (!self.walkthroughType) return 0
      const totalSteps = Walkthroughs[self.walkthroughType].length - 1
      return self.step / totalSteps
    },
    get isLastWalkthrough() {
      if (!self.walkthroughType) return 0
      return WalkthroughTypes.indexOf(self.walkthroughType) === WalkthroughTypes.length - 1
    },
    get isRaiseOrDealTraining() {
      if (!self.walkthroughType) return false
      return ['RaiseOrFold', 'DealTheFlop'].includes(self.walkthroughType)
    },
    get isRaiseOrFold() {
      if (!self.walkthroughType) return false
      return self.walkthroughType === 'RaiseOrFold'
    },
    get isDealTheFlop() {
      if (!self.walkthroughType) return false
      return ['DealTheFlop'].includes(self.walkthroughType)
    },
    get isTableTour() {
      if (!self.walkthroughType) return false
      return ['TableTour'].includes(self.walkthroughType)
    }
  }))
  .views((self) => ({
    get shouldDisplayWalkthroughOverlay() {
      if (self.walkthroughType === 'TableTour') {
        return self.step !== 4 && self.step !== 5
      }
      if (self.walkthroughType === 'RaiseOrFold') {
        return self.isFirstStep || self.isLastStep
      }

      if (self.walkthroughType === 'DealTheFlop') {
        return self.isFirstStep || self.isLastStep
      }
      return false
    }
  }))
  .actions((self) => ({
    setGameSnapshot() {
      if (!self.walkthroughType) return null
      const table = new Table(self.walkthroughType)
      table.players = WalkthroughPlayers(self.walkthroughType, self.step)
      table.board.communityCards = WalkthroughCommunityCards(self.walkthroughType, self.step)
      const snapshot = TableSnapshotter.forTable(table)
      snapshot.table[0].mainPot = 12345
      self.rootStore.game.game = GameSnapshotModel.create({
        ...snapshot,
        gameState: {
          //@ts-ignore
          tag: GameStates.Playing.tag,
          description: GameStates.Playing.description
        }
      })
    },
    addTableTourTargetPosition(key: TableTourStepsPositionKey, position: Position) {
      const step = TableTourStepsPositions[key]
      const newPosition = TargetPositionModel.create({
        step,
        position
      })
      self.targetPositions.replace([...self.targetPositions.filter((p) => p.step !== step), newPosition])
    },
    clearTargetPositions() {
      self.targetPositions.replace([])
    },
    clearWalkthrough() {
      self.walkthroughType = undefined
      self.step = 0
    }
  }))
  .actions((self) => ({
    setWalkthroughType(selectedType: WalkthroughType) {
      self.walkthroughType = selectedType
      self.step = 0
      self.setGameSnapshot()
    },
    setIsReponseVisible(value: boolean) {
      self.isResponseVisible = value
    },
    togleIsReponseVisible() {
      self.isResponseVisible = !self.isResponseVisible
    },
    goToNextStep() {
      if (!self.walkthroughType) return null
      const currentSteps = Walkthroughs[self.walkthroughType]
      const nextStep = self.step + 1
      if (currentSteps[nextStep]) {
        self.step = nextStep
      }
      self.setGameSnapshot()
    },
    goToPreviousStep() {
      if (!self.walkthroughType) return null
      const currentSteps = Walkthroughs[self.walkthroughType]
      const nextStep = self.step - 1
      if (currentSteps[nextStep]) {
        self.step = nextStep
      }
      self.setGameSnapshot()
    }
  }))

/**
 * The GameModalQueueStore instance.
 */
export type GameWalkthroughStore = Instance<typeof GameWalkthrough>

/**
 * The data of a GameModalQueueStore.
 */
export type GameWalkthroughSnapshot = SnapshotOut<typeof GameWalkthrough>
